import { ReactNode } from 'react';

import {
    Box,
    Grid,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    Skeleton,
    Typography,
} from '@mui/material';
import Icon from '@apigrowfinance/pbolt-icon';
import { InactiveAddress } from '@apigrowfinance/pbolt-icons';
import Tag from '@apigrowfinance/pbolt-tag';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { RiMapPinLine } from 'react-icons/ri';

import { AddressDetails } from '@clients/accountsClient';
import { FormatAddress } from '@utils/addressFormatter.ts';
import getEnumTranslation from '@utils/getEnumTranslation';

interface LocationRowProps {
    title: ReactNode;
    locations: AddressDetails[];
    selectedId?: number;
    handleClick?: (id: number) => void;
    isLoading?: boolean;
}

const LocationRow = ({
    title,
    locations,
    selectedId,
    handleClick,
    isLoading,
}: LocationRowProps) => {
    const { theme } = useTheme();

    return (
        <Grid container flexDirection="column">
            <Grid item sx={{ mb: 1.875 }}>
                {title}
            </Grid>
            {locations.map((location) => {
                const locationTitle = isLoading ? (
                    <Skeleton
                        width={theme.spacing(20)}
                        data-testid="location-block-title-loading"
                    />
                ) : (
                    FormatAddress({
                        type: 'title',
                        address: location,
                    })
                );
                const locationDescriptive = isLoading ? (
                    <Skeleton
                        width={theme.spacing(20)}
                        data-testid="location-block-descriptive-loading"
                    />
                ) : (
                    FormatAddress({
                        type: 'descriptive',
                        address: location,
                    })
                );
                return (
                    <ListItemButton
                        selected={selectedId === location.id}
                        onClick={() => handleClick(location.id)}
                        key={location?.id || 'loader'}
                        sx={{
                            mr: 0.96875,
                            borderRadius: theme.shape.borderRadius / 6,
                            mb: 3.125,
                            backgroundColor:
                                selectedId === location.id
                                    ? theme.palette.primary.main
                                    : theme.palette.background.paper,
                        }}>
                        <ListItemAvatar>
                            <Icon
                                data-testid="avatar"
                                avatarProps={{
                                    sx: {
                                        color:
                                            selectedId === location.id
                                                ? theme.palette.mode == 'dark'
                                                    ? 'inherit'
                                                    : theme.palette.primary.main
                                                : 'inherit',
                                    },
                                }}>
                                <RiMapPinLine />
                            </Icon>
                        </ListItemAvatar>
                        <Box display="flex" sx={{ ml: 0.5 }}>
                            <Box>
                                <ListItemText
                                    sx={{ alignContent: 'flex-start' }}
                                    primary={
                                        <Typography
                                            variant="h6"
                                            component="h2"
                                            sx={{
                                                color:
                                                    selectedId === location.id
                                                        ? theme.palette.primary
                                                              .contrastText
                                                        : theme?.custom?.brand
                                                              ?.textMain ||
                                                          'text.primary',
                                            }}>
                                            {locationTitle}
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color:
                                                    selectedId === location.id
                                                        ? theme.palette.primary
                                                              .contrastText
                                                        : theme.palette.text
                                                              .primary,
                                            }}>
                                            {locationDescriptive}
                                        </Typography>
                                    }
                                />
                            </Box>
                        </Box>
                        {!location?.active && (
                            <Box sx={{ ml: 'auto', mr: 0 }}>
                                <Tag
                                    icon={
                                        <InactiveAddress
                                            sx={{ font: 'inherit' }}
                                            width={theme.spacing(0.375)}
                                        />
                                    }
                                    label={getEnumTranslation('inactive')}
                                    color="error"
                                    filled
                                />
                            </Box>
                        )}
                        {selectedId === location.id && (
                            <Box
                                sx={{
                                    borderTop: `${theme.spacing(
                                        0.8125,
                                    )} solid transparent`,
                                    borderLeft: `${theme.spacing(
                                        1.125,
                                    )} solid ${theme.palette.primary.main}`,
                                    borderBottom: `${theme.spacing(
                                        0.8125,
                                    )} solid transparent`,
                                    position: 'absolute',
                                    right: theme.spacing(-1),
                                }}
                            />
                        )}
                    </ListItemButton>
                );
            })}
        </Grid>
    );
};

export default LocationRow;
